import { ref, computed } from 'vue';
import indumentariaService from '@/services/admin/bases/indumentarias';

export const INVENTARIO_TABS = {
    vendidas: 'vendidas',
    stock: 'stock',
    recogidas: 'recogidas',
};

/**
 * Flattens the API response { nombre, tallas: [...] }
 * into table rows [{ prenda, talla, cantidad }, ...].
 * For recogidas, also includes sin_recoger field.
 */
function flattenIndumentarias(items, cantidadKey = 'cantidad', extraKey = null) {
    const rows = [];
    for (const item of items) {
        const prenda = item.nombre || '';
        const tallas = Array.isArray(item.tallas) ? item.tallas : [];
        for (const t of tallas) {
            const row = {
                prenda,
                talla: t.nombre || '',
                cantidad: t[cantidadKey] ?? 0,
            };
            if (extraKey) {
                row[extraKey] = t[extraKey] ?? 0;
            }
            rows.push(row);
        }
    }
    return rows;
}

export function useIndumentariaInventario(listaId) {
    const tabsData = ref({
        [INVENTARIO_TABS.vendidas]: { items: [], meta: {} },
        [INVENTARIO_TABS.stock]: { items: [], meta: {} },
        [INVENTARIO_TABS.recogidas]: { items: [], meta: {} },
    });

    const loading = ref(false);
    const indumentariaImages = ref({});

    const activeTabKey = ref(INVENTARIO_TABS.vendidas);

    const currentTabData = computed(() => tabsData.value[activeTabKey.value]);

    async function fetchTabData(tab, page = 1, perPage = 10) {
        loading.value = true;

        try {
            let result;
            let cantidadKey = 'cantidad';
            let extraKey = null;

            switch (tab) {
                case INVENTARIO_TABS.vendidas:
                    result = await indumentariaService.getInventarioVendidas({
                        lista_id: listaId.value,
                        page,
                        per_page: perPage,
                    });
                    break;
                case INVENTARIO_TABS.stock:
                    result = await indumentariaService.getInventarioStock({
                        lista_id: listaId.value,
                        page,
                        per_page: perPage,
                    });
                    break;
                case INVENTARIO_TABS.recogidas:
                    result = await indumentariaService.getInventarioRecogidas({
                        lista_id: listaId.value,
                        page,
                        per_page: perPage,
                    });
                    cantidadKey = 'recogidas';
                    extraKey = 'sin_recoger';
                    break;
                default:
                    result = { items: [], meta: {} };
            }

            const flatItems = flattenIndumentarias(result.items, cantidadKey, extraKey);

            tabsData.value = {
                ...tabsData.value,
                [tab]: { items: flatItems, meta: result.meta },
            };
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(`[useIndumentariaInventario] Error fetching tab ${tab}:`, error);
            }
            tabsData.value = {
                ...tabsData.value,
                [tab]: { items: [], meta: {} },
            };
        } finally {
            loading.value = false;
        }
    }

    function onTabChange(tab) {
        activeTabKey.value = tab;

        const current = tabsData.value[tab];
        if (!current.items.length && !current.meta.total) {
            fetchTabData(tab);
        }
    }

    function init() {
        fetchTabData(INVENTARIO_TABS.vendidas);
        fetchImages();
    }

    async function fetchImages() {
        try {
            const result = await indumentariaService.getIndumentarias({
                lista_id: listaId.value,
                per_page: 100,
            });
            const map = {};
            for (const item of result.items) {
                if (item.imagen_formatted || item.imagen) {
                    map[item.nombre] = item.imagen_formatted || item.imagen;
                }
            }
            indumentariaImages.value = map;
        } catch (e) {
            // silently ignore
        }
    }

    function getImageUrl(prenda) {
        return indumentariaImages.value[prenda] || null;
    }

    return {
        activeTabKey,
        currentTabData,
        loading,
        tabsData,
        onTabChange,
        init,
        getImageUrl,
    };
}
